import { useEffect } from "react";

import type { PlanogramBay } from "@CommonTypes/merchflow/pog/bay";
import { Color } from "src/elements";
import { useTranslation } from "src/i18n";
import { SPLIT_GAP } from "src/components/Planogram/components/KonvaPlanogram/sceneProjection";
import { ThreePlanogram } from "../../store/types";
import { createThreeMesh, createThreeText } from "../../store/utils/three";
import { useSplitShift } from "../../store/splitShift";

interface Props {
  bay: PlanogramBay;
  three: ThreePlanogram;
}

/**
 * The caption's glyph size, in pog units: it fits the gap right of the divider. A literal:
 * `SPLIT_GAP` is still undefined while the import cycle through the 2D scene projection is
 * evaluating this module.
 */
export const AISLE_BREAK_LABEL_SIZE = 200;

/**
 * The break in the run, standing in the empty space between this bay and the
 * next one: a dashed line the height of the bay, so the gap reads as a corner
 * or a doorway rather than a hole in the drawing, and the 2D canvas's caption
 * ("Aisle break") just right of the line, reading down from the top of the bay.
 */
export const AisleBreak = ({ bay, three }: Props) => {
  const toSceneX = useSplitShift();
  const { t } = useTranslation("common");
  const label = t("cdtPriorityOrder.aisleBreak");

  useEffect(() => {
    // Never more than a third of the gap, so the divider stands clear of the bays either side.
    const dividerWidth = Math.min(100, SPLIT_GAP / 3);
    const gapCenterX = toSceneX(bay.getXLeft()) + bay.width + SPLIT_GAP / 2;

    const dashHeight = 200;
    const dashGap = 200;
    const dashCount = Math.floor(bay.height / (dashHeight + dashGap));
    const cleanups: Array<() => void> = [];

    for (let dashIndex = 0; dashIndex < dashCount; dashIndex += 1) {
      const dash = createThreeMesh({
        scene: three.scene,
        width: dividerWidth,
        height: dashHeight,
        depth: bay.depth,
        x: gapCenterX - dividerWidth / 2,
        y: dashIndex * (dashHeight + dashGap) + dashHeight / 2,
        z: 0,
        color: Color.spaceGray,
        renderOrder: 3,
      });

      cleanups.push(dash.cleanUp);
    }

    const caption = createThreeText({
      scene: three.scene,
      text: label,
      size: AISLE_BREAK_LABEL_SIZE,
      // Turned a quarter clockwise, the glyphs stand to the right of their start.
      x: gapCenterX + dividerWidth / 2 + 1,
      y: bay.height - AISLE_BREAK_LABEL_SIZE,
      z: bay.depth + 1,
      color: Color.spaceGray,
      rotationZ: -Math.PI / 2,
      renderOrder: 3,
    });
    if (caption) cleanups.push(caption.cleanUp);

    return () => {
      cleanups.forEach((cleanUp) => cleanUp());
    };
  }, [bay, label, three, toSceneX]);

  return null;
};
